import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  WrenchScrewdriverIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { remoteConfig } from '../../utility/firebaseRemoteConfigHelper';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { useAuth } from '../../auth/firebaseAuth';
import { db } from '../../auth/firebaseConfig';
import {
  addDoc,
  collection,
  getDocs,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { toast } from 'react-hot-toast';

type TenancyItem = {
  id: string;
  listingId: string;
  rentAmount: number;
  landlordId?: string;
  tenantInfo: { name: string; email: string; phone?: string };
  propertyInfo: { title: string; address: string };
  landlordEmail?: string; // optional if available
  landlordName?: string; // optional if available
};

const TenantToolkitPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isEnabled = remoteConfig.isTenantToolkitEnabled();

  const [showMessagingModal, setShowMessagingModal] = useState(false);
  const [loadingTenancies, setLoadingTenancies] = useState(false);
  const [tenancies, setTenancies] = useState<TenancyItem[]>([]);
  const [selectedTenancyId, setSelectedTenancyId] = useState<string>('');
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Derive the selected tenancy object based on the ID
  const selectedTenancy = useMemo(() => {
    return tenancies.find((t) => t.id === selectedTenancyId);
  }, [tenancies, selectedTenancyId]);

  const messageTemplates = useMemo(
    () => [
      "I'm interested in scheduling a viewing. Could you share available dates/times?",
      'Could you provide more details about utilities and any extra costs?',
      'Is there flexibility on the move-in date for this property?',
      'I have questions about the lease terms. Can we arrange a quick call?'
    ],
    []
  );

  const onOpenMessaging = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    setShowMessagingModal(true);
    // Fetch tenant's tenancies from Cloud Function
    setLoadingTenancies(true);
    setError(null);
    try {
      const url = `${CLOUD_FUNCTIONS_BASE_URL}/getTenanciesForTenant?tenantId=${encodeURIComponent(
        currentUser.uid
      )}`;
      const token = await currentUser.getIdToken();
      const res = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to fetch tenancies');
      }
      const data = await res.json();
      // Endpoint returns { success: true, tenancies: [...] }
      const items: TenancyItem[] = (data?.tenancies || []).map((t: any) => ({
        id: t.id || t.tenancyId || '',
        listingId: t.listingId,
        rentAmount: t.rentAmount,
        landlordId: t.landlordId,
        tenantInfo: t.tenantInfo,
        propertyInfo: t.propertyInfo,
        landlordEmail: t.landlordEmail,
        landlordName: t.landlordName
      }));
      setTenancies(items);
      if (items.length > 0) setSelectedTenancyId(items[0].id);
      toast.success('Loaded your active tenancies');
    } catch (e: any) {
      setError(e.message || 'Something went wrong loading tenancies');
      toast.error(e.message || 'Failed to load tenancies');
    } finally {
      setLoadingTenancies(false);
    }
  };

  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedTemplate(value);
    if (value) setMessage(value);
  };

  const startConversation = async () => {
    if (!currentUser || !selectedTenancy) return;

    if (!message || message.length < 2) {
      toast.error('Please enter a message to send.');
      return;
    }

    setIsSending(true);
    try {
      // Create or append to conversation
      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', currentUser.uid),
        where('listingId', '==', selectedTenancy.listingId)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        await addDoc(conversationsRef, {
          participants: [currentUser.uid, selectedTenancy.landlordId].filter(
            Boolean
          ),
          listingId: selectedTenancy.listingId,
          propertyTitle: selectedTenancy.propertyInfo?.title || 'Property',
          lastMessage: message,
          lastMessageTimestamp: serverTimestamp()
        });
      } else {
        const conversationDoc = snapshot.docs[0];
        // Add message to subcollection
        await addDoc(
          collection(db, 'conversations', conversationDoc.id, 'messages'),
          {
            senderId: currentUser.uid,
            recipientId: selectedTenancy.landlordId || 'landlord',
            content: message,
            timestamp: serverTimestamp(),
            senderName: currentUser.displayName || '',
            senderEmail: currentUser.email || '',
            isGuestInquiry: false,
            listingId: selectedTenancy.listingId,
            propertyTitle: selectedTenancy.propertyInfo?.title || 'Property',
            archived: false
          }
        );
      }
      setShowMessagingModal(false);
      setMessage('');
      setSelectedTemplate('');
      toast.success('Message sent to landlord');
      navigate('/messages');
    } catch (e: any) {
      setError(e.message || 'Failed to send message');
      toast.error(e.message || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  // If tenant toolkit is disabled, show unavailable message
  if (!isEnabled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4 flex items-center justify-center">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center border border-gray-100">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-100 rounded-full mb-6">
              <ExclamationTriangleIcon className="h-10 w-10 text-amber-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tenant Toolkit Currently Unavailable
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              The Tenant Toolkit is temporarily unavailable. We're working on
              bringing you an enhanced experience.
            </p>
            <p className="text-gray-500 mb-8">
              In the meantime, you can still access your messages and profile.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/browse')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Browse properties
              </button>
              <button
                onClick={() => navigate('/property-tools/property-alerts')}
                className="px-6 py-3 bg-white text-blue-600 border-2 border-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Get Property alerts
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const toolkitFeatures = [
    {
      id: 'maintenance-requests',
      icon: WrenchScrewdriverIcon,
      title: 'Maintenance Requests',
      description:
        'Submit and track maintenance requests for your rental property. Upload photos, add descriptions, and monitor the status of your requests.',
      status: 'Available Now',
      color: 'bg-blue-50 text-blue-600',
      borderColor: 'border-blue-200',
      comingSoon: false,
      actionPath: '/tenant/maintenance',
      features: [
        'Submit requests with photos',
        'Track request status',
        'View repair history'
      ]
    },
    {
      id: 'messaging',
      icon: ChatBubbleLeftRightIcon,
      title: 'Messaging',
      description:
        'Communicate directly with your landlord through our secure messaging system. Keep all conversations organized and accessible.',
      status: 'Available Now',
      color: 'bg-purple-50 text-purple-600',
      borderColor: 'border-purple-200',
      comingSoon: false,
      actionPath: '',
      features: [
        'Secure messaging',
        'Message history',
        'Real-time notifications'
      ]
    }
  ];

  const handleFeatureClick = (feature: (typeof toolkitFeatures)[0]) => {
    if (feature.comingSoon) {
      return; // Do nothing for coming soon features
    }
    if (feature.id === 'messaging') {
      onOpenMessaging();
      return;
    }
    if (feature.actionPath) {
      navigate(feature.actionPath);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Tenant Toolkit
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to manage your tenancy in one place. Request
            maintenance, communicate with your landlord, and more.
          </p>
        </div>

        {/* Toolkit Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {toolkitFeatures.map((feature) => (
            <div
              key={feature.id}
              className={`bg-white rounded-2xl shadow-lg p-8 border-2 ${feature.borderColor} hover:shadow-xl transition-all duration-300 ${
                !feature.comingSoon
                  ? 'cursor-pointer hover:scale-[1.02]'
                  : 'opacity-90'
              }`}
              onClick={() => handleFeatureClick(feature)}
            >
              {/* Header with Icon and Status */}
              <div className="flex items-start justify-between mb-4">
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-xl ${feature.color}`}
                >
                  <feature.icon className="h-8 w-8" />
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    feature.comingSoon
                      ? 'bg-gray-100 text-gray-600'
                      : 'bg-green-100 text-green-700'
                  }`}
                >
                  {feature.status}
                </span>
              </div>

              {/* Title and Description */}
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                {feature.description}
              </p>

              {/* Features List */}
              <ul className="space-y-2 mb-6">
                {feature.features.map((item, index) => (
                  <li key={index} className="flex items-center text-gray-700">
                    <ArrowRightIcon className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Action Button */}
              {!feature.comingSoon ? (
                <button
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFeatureClick(feature);
                  }}
                >
                  Open {feature.title}
                  <ArrowRightIcon className="h-5 w-5" />
                </button>
              ) : (
                <div className="w-full px-6 py-3 bg-gray-100 text-gray-500 rounded-lg font-semibold text-center cursor-not-allowed">
                  Coming Soon
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Help Section */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-2">Need help or have questions?</p>
          <button
            onClick={() => navigate('/help-center')}
            className="text-blue-600 hover:text-blue-700 font-semibold underline"
          >
            Visit our Help Center
          </button>
        </div>

        {showMessagingModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  Start a Message
                </h2>
                <button
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => setShowMessagingModal(false)}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              {selectedTenancy && (
                <p className="text-sm text-gray-600 mb-4">
                  To: {selectedTenancy.propertyInfo?.title || 'Property'} ·
                  Landlord: {selectedTenancy.landlordName || 'Unknown'}
                </p>
              )}

              {loadingTenancies ? (
                <p className="text-gray-600">Loading your properties…</p>
              ) : error ? (
                <p className="text-red-600">{error}</p>
              ) : tenancies.length === 0 ? (
                <p className="text-gray-600">
                  We couldn't find any active tenancies linked to your account.
                </p>
              ) : (
                <>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Select property
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4"
                    value={selectedTenancyId}
                    onChange={(e) => setSelectedTenancyId(e.target.value)}
                  >
                    {tenancies.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.propertyInfo?.title || 'Property'} — #{t.listingId}
                      </option>
                    ))}
                  </select>

                  <div className="mb-4">
                    <label className="block text-gray-700 font-semibold mb-2">
                      Quick template (optional)
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      value={selectedTemplate}
                      onChange={handleTemplateChange}
                    >
                      <option value="">Select a template…</option>
                      {messageTemplates.map((tpl, i) => (
                        <option key={i} value={tpl}>
                          {tpl}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 font-semibold mb-2">
                      Your message
                    </label>
                    <textarea
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 h-28"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Type your message to the landlord…"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Minimum 10 characters
                    </p>
                  </div>

                  {selectedTenancy?.landlordEmail && (
                    <div className="mb-4">
                      <a
                        href={`mailto:${selectedTenancy.landlordEmail}`}
                        className="text-blue-600 hover:text-blue-700 underline"
                      >
                        Or email the landlord directly (
                        {selectedTenancy.landlordEmail})
                      </a>
                    </div>
                  )}

                  <div className="flex gap-3 justify-end">
                    <button
                      className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                      onClick={() => setShowMessagingModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                      onClick={startConversation}
                      disabled={isSending}
                    >
                      {isSending ? 'Sending…' : 'Send Message'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantToolkitPage;
